export interface AnswerSegment {
  text: string;
  changed: boolean;
}

export interface AnswerDiff {
  student: AnswerSegment[];
  correct: AnswerSegment[];
}

const WHITESPACE_EDGES = /^(\s*)(.*?)(\s*)$/su;

const pushSegment = (segments: AnswerSegment[], text: string, changed: boolean) => {
  if (!text) return;
  const last = segments.at(-1);
  if (last?.changed === changed) {
    last.text += text;
  } else {
    segments.push({ text, changed });
  }
};

// AIDEV-NOTE: Mezery na okrajích změněného úseku se nezvýrazňují — rám u „1250“ × „1 200“
// svítí jen na číslicích. Rozdíl v mezeře student stejně nezavinil. [TD-334]
const toSegments = (chars: string[], changed: boolean[]): AnswerSegment[] => {
  const runs: AnswerSegment[] = [];
  for (const [index, char] of chars.entries()) {
    pushSegment(runs, char, changed[index]);
  }

  const segments: AnswerSegment[] = [];
  for (const run of runs) {
    if (!run.changed) {
      pushSegment(segments, run.text, false);
      continue;
    }
    const [, lead, core, trail] = WHITESPACE_EDGES.exec(run.text) ?? ["", "", run.text, ""];
    pushSegment(segments, lead, false);
    pushSegment(segments, core, true);
    pushSegment(segments, trail, false);
  }
  return segments;
};

// Znakové LCS. Odpovědi mají jednotky až desítky znaků, O(n·m) nevadí a závislost
// na diff knihovně by tu nic nepřinesla.
export const diffAnswers = (student?: string | null, correct?: string | null): AnswerDiff => {
  const a = Array.from(student ?? "");
  const b = Array.from(correct ?? "");

  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const aChanged: boolean[] = [];
  const bChanged: boolean[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      aChanged.push(false);
      bChanged.push(false);
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      aChanged.push(true);
      i++;
    } else {
      bChanged.push(true);
      j++;
    }
  }
  while (i++ < a.length) aChanged.push(true);
  while (j++ < b.length) bChanged.push(true);

  return { student: toSegments(a, aChanged), correct: toSegments(b, bChanged) };
};
